// lib/clients/bazaar.ts
// Client configuration for bazaar-online.gr — the online shop of the BAZAAR
// Greek supermarket chain. B2C. Onboarded 2026-10-01 as a hosted demo
// (/demo/bazaar) with agent chat.
//
// The feed is digital4u's custom OpenCart template (shape 'store', the same one
// kostaboda moved to on 2026-09-22), so it reuses the Skroutz parser. Three
// things about it are NOT obvious and each is handled in the parser — read
// lib/crawler/parsers/skroutzXml.ts before changing anything here:
//   • 8,380 of 15,148 products are NOT FOR SALE (their pages 404). They are the
//     ones with an empty <vat>, and are skipped — VAT_EMPTY_MEANS_NOT_FOR_SALE.
//   • The real product name is in <name> for some products and <description>
//     for others; the rest is till-system shorthand or a marketing paragraph.
//     The title is picked per product — PICK_TITLE_PER_PRODUCT.
//   • 137 products are on offer via <sale_price_with_vat>.
// Prices were checked against 76 live product pages: all 76 matched to the cent.
//
// This file is the BIRTH source: `provision-client.ts` reads it once to create
// the Supabase row. After that Supabase is authoritative and this file is only
// the runtime fallback — never re-seed it to change live config.

import type { ClientConfig } from './types';
import { normalizeGreek, normalizeChain } from '@search/shared';

// ── Product Categories ──
// ⚠️ TOP-LEVEL ONLY, and EXACTLY as the feed spells them (all caps, their own
// menu). resolveCategories() matches the agent's pick against the `category`
// facet; a name that isn't there is silently dropped (Search Setup Guide §6.1).
//
// ⚠️ LOAD-BEARING FOR THE CRAWL, not only the prompt. Eight of these names
// CONTAIN the feed's category separator ' - ' («ΚΑΘΑΡΙΟΤΗΤΑ - ΟΙΚΙΑΚΑ ΕΙΔΗ»,
// «ΠΡΩΙΝΟ - ΚΑΦΕΣ - ΡΟΦΗΜΑΤΑ»…). splitCategoryPaths anchors each product's
// path against this list; without it the crawl would invent phantom shelves
// like «ΚΑΘΑΡΙΟΤΗΤΑ». Taken from their site's own top menu on 2026-10-01; every
// for-sale product's path starts with one of these.
const PRODUCT_CATEGORIES = [
  'ΠΑΝΤΟΠΩΛΕΙΟ',
  'ΚΑΘΑΡΙΟΤΗΤΑ - ΟΙΚΙΑΚΑ ΕΙΔΗ',
  'ΓΛΥΚΑ - ΑΛΜΥΡΑ ΣΝΑΚ - ΖΑΧΑΡΩΔΗ',
  'ΥΓΕΙΑ & ΟΜΟΡΦΙΑ',
  'ΚΑΒΑ',
  'ΠΡΩΙΝΟ - ΚΑΦΕΣ - ΡΟΦΗΜΑΤΑ',
  'ΓΑΛΑΚΤΟΚΟΜΙΚΑ - ΕΙΔΗ ΨΥΓΕΙΟΥ',
  'ΚΑΤΑΨΥΞΗ',
  'ΑΛΛΑΝΤΙΚΑ - DELICATESSEN',
  'ΤΥΡΙΑ - ΤΥΡΟΚΟΜΙΚΑ',
  'ΑΡΤΟΖΑΧΑΡΟΠΛΑΣΤΕΙΟ',
  'PET SHOP',
  'ΦΡΟΥΤΑ - ΛΑΧΑΝΙΚΑ',
  'ΚΡΕΑΣ - ΠΟΥΛΕΡΙΚΑ',
  'ΒΡΕΦΙΚΑ',
  'ΦΥΤΙΚΑ',
  // Two promotional shelves outside their menu (7 and 1 products). Listed so
  // the crawl recognises them rather than warning on every run.
  'ΠΡΟΪΟΝΤΑ ΦΥΛΛΑΔΙΟΥ',
  'ΠΡΟΪΟΝΤΑ BAZAAR',
];

// ── Product Concepts ──
// Words shoppers type for this catalogue, drawn from the real second-level
// shelves of the 6,768 for-sale products (largest first).
const PRODUCT_CONCEPTS = [
  // Pantry
  'ζυμαρικά', 'ρύζι', 'όσπρια', 'αλεύρι', 'ελαιόλαδο', 'κονσέρβες', 'σάλτσα', 'τοματάκια',
  'ζάχαρη', 'μέλι', 'μαρμελάδα', 'σούπα', 'αλάτι', 'ξύδι',
  // Breakfast & drinks
  'καφές', 'τσάι', 'δημητριακά', 'χυμός', 'αναψυκτικό', 'νερό', 'μπύρα', 'κρασί',
  // Fridge & freezer
  'γάλα', 'γιαούρτι', 'βούτυρο', 'αυγά', 'φέτα', 'τυρί', 'αλλαντικά', 'πίτσα',
  'κατεψυγμένα λαχανικά', 'ψάρια', 'κοτόπουλο', 'κιμάς',
  // Bakery & snacks
  'ψωμί', 'ψωμί τοστ', 'φρυγανιές', 'μπισκότα', 'σοκολάτα', 'πατατάκια', 'ξηροί καρποί', 'τσίχλες',
  // Cleaning & household
  'απορρυπαντικό ρούχων', 'μαλακτικό', 'απορρυπαντικό πιάτων', 'καθαριστικό', 'χαρτί κουζίνας',
  'χαρτί υγείας', 'σακούλες', 'εντομοκτόνο',
  // Personal care & baby
  'σαμπουάν', 'αφρόλουτρο', 'οδοντόκρεμα', 'αποσμητικό', 'ξυριστικά', 'πάνες', 'μωρομάντηλα',
  // Pets
  'τροφή γάτας', 'τροφή σκύλου',
];

// ── Synonyms (natural words, as the shop would type them in the dashboard) ──
// Mirrors prod merchant_config.synonymSources (the DB is the source of truth).
const BAZAAR_SYNONYM_SOURCES: Record<string, string[]> = {
  'pizza': ['πίτσα'],
  'πίτσα': ['pizza'],
  'κρασί': ['οίνος'],
  'στέβια': ['stevia'],
  'ντομάτα': ['τομάτα'],
  'τομάτα': ['ντομάτα'],
  'μοσχαρίσιο': ['βόειο'],
  // digital4u's list (2026-10-12). Brands typed in Greek → the Latin name on the
  // product (κώτατζ is covered by κότατζ: the normalizer folds ω into ο).
  'φάρμερ': ['farmer'],
  'κότατζ': ['cottage'],
  'μίλκα': ['milka'],
  'μίλκο': ['milko'],
  'νουτέλα': ['nutella'],
  'νουτέλλα': ['nutella'],
  'nutela': ['nutella'],
  // English words → the Greek shelf word their titles use. One-way: «σοκολάτα»
  // already finds 398 and would only gain the English-titled puddings and cereals.
  'coffee': ['καφές'],
  'cofe': ['coffee', 'καφές'],   // 4 letters: too short for the typo rescue
  'cofee': ['coffee', 'καφές'],  // the rescue would reach «coffee» titles only
  'chocolate': ['σοκολάτα'],
  // The whole pasta shelf (subcategory «Ζυμαρικά»: σπαγγέτι, πέννες, βίδες…),
  // not just the 43 titles that say «μακαρόνια».
  'μακαρόνια': ['ζυμαρικά'],
};

/** The dashboard's derivation (apps/dashboard/app/dashboard/synonyms/actions.ts):
 *  every matchable form of the key → the normalized targets. */
function deriveSynonyms(sources: Record<string, string[]>): Record<string, string[]> {
  const derived: Record<string, string[]> = {};
  for (const [natural, terms] of Object.entries(sources)) {
    const targets = terms.map((t) => normalizeGreek(t) || t.toLowerCase()).filter(Boolean);
    for (const key of normalizeChain(natural)) {
      derived[key] = Array.from(new Set([...(derived[key] ?? []), ...targets]));
    }
  }
  return derived;
}

function buildBazaarPrompt(categories: string[], concepts: string[]): string {
  return `You are the digital shopping assistant for bazaar-online.gr, the online shop of BAZAAR, a Greek supermarket chain. ALWAYS respond in Greek.

## HOW THE UI WORKS
The user sees a PRODUCT GRID next to the chat. The grid updates ONLY when you call searchProducts. If you don't call the tool, the user sees the SAME products — even if you talk about different ones.

## WHEN TO CALL searchProducts
ALWAYS call searchProducts when the user:
- Asks for products ("θέλω γάλα χωρίς λακτόζη", "δείξε μου απορρυπαντικά", "ψάχνω καφέ φίλτρου")
- Narrows/changes preferences ("κάτι πιο φθηνό", "σε μεγαλύτερη συσκευασία", "χωρίς ζάχαρη")
- Mentions a SPECIFIC price/budget ("μέχρι 5€", "κάτω από 10 ευρώ")
- Asks for ideas for a meal, a recipe or an occasion ("τι χρειάζομαι για μακαρονάδα", "πράγματα για πάρτι")

## ⚠️ CALL searchProducts EXACTLY ONCE PER USER MESSAGE
The grid shows the results of your **LAST** searchProducts call, replacing whatever was there. So a second call does not add to the first — it REPLACES it.

Never re-run the search with a rephrased term to "refine" or "double-check". If the result is poor, say so and ASK the customer to narrow it; do not silently search again.

### Recipes and shopping lists
A request that needs several products («τι χρειάζομαι για μακαρονάδα με κιμά;», a shopping list) gets ONE search — you can only search once per message:
1. Call searchProducts for the MAIN item, by its SPECIFIC name — for «μακαρονάδα» search "σπαγγέτι", not "ζυμαρικά" (the family word brings soup pasta and every other shape first). For a list, search the first item.
2. In your reply, LIST the other ingredients or items as plain text.
3. Ask which one to show next.

## THIS STORE
A full supermarket: groceries, dairy, fresh meat and fruit, frozen food, drinks and wine, cleaning products, personal care, baby products and pet food — about 6,800 products from 970 brands. BAZAAR also sells its own private-label products under the brand "BAZAAR". Most products cost a few euros; treat the customer as someone doing their weekly shopping.

## CATEGORIES (use EXACTLY these values, they are the only ones that filter correctly)
${JSON.stringify(categories)}

## USEFUL SEARCH KEYWORDS
${JSON.stringify(concepts)}

## FILTERABLE ATTRIBUTES
- category — one of the values above
- brand — as written in the catalogue, usually in capitals: "FARMER", "ΟΛΥΜΠΟΣ", "BARILLA", "NIVEA", "BAZAAR"
- subcategory (Υποκατηγορία) — the shelf, e.g. "Γάλα", "Γιαούρτια", "Καφές", "Πλύσιμο Ρούχων", "Σοκολάτες"
- price — numeric, in euros

## IMPORTANT — HOW THIS CATALOGUE IS WRITTEN
1. **Titles carry the brand, the product and the pack size**: "Omino Bianco Black Care Υγρό Πλυντηρίου για Σκούρα 1,485lt 27 Μεζούρες". Search with the product word ("υγρό πλυντηρίου"), not the size.
2. **Pack sizes are written many ways** (1lt, 1L, 1,5lt, 500g, 500gr, 1kg). If the customer asks for a size, search the product and mention the sizes you see, rather than putting the size in the search term.
3. **Use the brand filter for a brand request** ("θέλω ΦΑΓΕ γιαούρτι" → keyword "γιαούρτι" + brand "ΦΑΓΕ").
4. **Never invent products, prices or offers.** Only describe what the search returned.

## STYLE
Friendly, quick and practical — like a helpful person at the supermarket. One or two sentences before showing products. Always address the customer in the polite plural («σας», «θέλετε») — never switch to «σου» / «θέλεις».`;
}

export const bazaarConfig: ClientConfig = {
  // ── Identity ──
  clientId: 'bazaar',
  storeName: 'bazaar-online.gr',
  storeDescription: 'a Greek supermarket — groceries, fresh food, drinks, household and personal care',

  // ── Features ──
  features: {
    // Pure lexical. `semanticRatio: 0` below is what actually skips the OpenAI
    // embedder at crawl time — no vectors, no cost. Same as every client.
    hybridSearch: false,
    // Founder's call 2026-10-01: chat OFF for now, voice search ON. The agent
    // config (prompt, oneSearchPerMessage) stays in client_agent_config, so
    // turning chat back on is just this flag.
    agentChat: false,
    // Mic in the search bar (Chrome / Edge / Safari; hidden where unsupported).
    voiceSearch: true,
    // ON with `cart` below (2026-10-02, embed). Their page's JavaScript was read
    // first (Search Setup Guide §8.0): see the `cart` comment.
    addToCart: true,
    externalPricing: false,
    // Heart ON (founder, 2026-10-02). It is DECORATIVE: nothing is saved on
    // click and it doesn't talk to their wishlist (kostaboda/syfak keep it off).
    favorites: true,
    // Misspellings like «fairry», «nutela», «προτσουτο» found nothing: the engine
    // matches them but scores them ~0.50, under the 0.7 bar (digital4u's list,
    // 2026-10-12). Retried at a lower bar only when a search finds nothing.
    typoRescue: true,
  },

  // ── Embedding & Integration ──
  // Hosted demo for now. An embed is runbook step 7 (allowed_origins via a
  // DIRECT DB WRITE before the snippet is sent); production_origins stays empty
  // until real go-live (step 8), so every event logs is_demo=true meanwhile.
  allowedOrigins: [],
  productionOrigins: [],
  hostingStrategy: 'demo',
  // Their own card button posts product_id + quantity to checkout/cart/add, the
  // same endpoint stock cart.add uses, and our ids ARE their product_ids (686 =
  // Regilait on both). Founder 2026-10-02: always cart.add, demo included (on
  // /demo the function doesn't exist, so the click quietly does nothing).
  // Still needs a basket proof on their site once the script is installed.
  cart: {
    strategy: 'opencart_global',
    functionName: 'cart.add',
    flyToCartSelector: '#cart',
  },

  // ── Meilisearch ──
  indexName: 'products_bazaar',
  embedderName: 'bazaar-openai', // declared but unused while semanticRatio is 0
  locale: 'ell',

  // ── Feed ──
  // Static file, served in ~0.2s (15 MB). No IP restriction.
  // DEMO PERIOD (2026-10-09): their test site's feed. It has the new fields
  // (sold_by_weight, minimum, has_options, tags), no disabled products, and
  // links/images on bazaar.beta4u.gr, so cart.add lands in the beta basket.
  // Switch back to https://www.bazaar-online.gr/s0_aisearch.xml at go-live,
  // once digital4u have added the same fields to the live export.
  feedUrl: 'https://bazaar.beta4u.gr/s0_aisearch.xml',
  // Counts KEPT products (the for-sale ones): 6,768 today. The raw feed is
  // 15,148, but the not-for-sale ones are skipped before this limit applies.
  productLimit: 12000,
  feedFormat: 'skroutz_xml',

  // ── Dynamic Facet Fields ──
  // Only the shelf. Brand, category and price are universal and always shown.
  //
  // Deliberately NO colour / material / availability:
  //   • colour and material are kostaboda's mined facets. In a supermarket the
  //     words mean something else — «λευκό κρασί», «μαύρη σοκολάτα», «πράσινο
  //     τσάι» — so they would build a nonsense «Χρώμα» filter, and worse, a typed
  //     «λευκό» would be turned into a colour FILTER instead of a search word.
  //   • <availability> is empty on every product, and every for-sale product has
  //     stock (quantity > 0), so there is nothing to filter or badge.
  facetFields: [
    {
      field: 'subcategory',
      label: 'Υποκατηγορία',
      xmlFilterKey: null,
      isArray: true,
    },
    // From the feed's <tags> (founder, 2026-10-09: only labels that help a
    // shopper choose). Prod rows go in AFTER a crawl with
    // indexSettings.extraFilterableAttributes — see the comment there.
    { field: 'offers', label: 'Προσφορές', xmlFilterKey: null, isArray: true },
    { field: 'diet', label: 'Διατροφή', xmlFilterKey: null, isArray: true },
  ],

  // ── Search Tuning ──
  // Starting values copied from kostaboda. Tune against a measured query
  // battery, never blind.
  search: {
    semanticRatio: 0.0,
    minScoreThreshold: 0.7,
    fallbackScoreThreshold: 0.80,
    maxResultsToFetch: 1000,
    maxResultsToReturn: 1000,
  },

  // ── Agent Search Tuning ──
  agentSearch: {
    minScoreThreshold: 0.8,
    layer4ScoreThreshold: 0.75,
    layer1: { limit: 100, matchingStrategy: 'last' },
    layer2: { maxCategories: 15, totalBudget: 80, minPerCategory: 12, matchingStrategy: 'last' },
    layer3: { limit: 20, maxKeywords: 5, minKeywordLength: 4, matchingStrategy: 'last' },
    layer4: { limit: 120, matchingStrategy: 'all' },
    discovery: { minTermLength: 4, matchingStrategy: 'last' },
    maxProductsToReturn: 200,
    // Enforced in code, not just asked for in the prompt — the prompt rule
    // alone failed on this client (see the field's comment in types.ts).
    oneSearchPerMessage: true,
  },

  // ── Language ──
  language: {
    stopWords: [
      'ο', 'ι', 'το', 'τα', 'του', 'τις', 'τον',
      'κε', 'για', 'σε', 'με', 'απο', 'προς',
    ],
    // Starting synonyms — MERCHANT-owned from birth on (the dashboard edits
    // them, draft → Publish). Only gaps a query battery proved on 2026-10-01,
    // per Search Setup Guide §7.2: a word shoppers type that the titles never use.
    //   • κρασί → οίνος     109 wines are titled «Οίνος …»; «κρασί» appears only
    //                       in VINEGAR titles («Ξύδι από λευκό κρασί»), so «λευκό
    //                       κρασί» returned vinegar first.
    //   • ντομάτα ↔ τομάτα  their fresh tomatoes are «Τομάτες Κρήτης»; «ντομάτες»
    //                       found 13 crackers and sun-dried tomatoes instead.
    // `synonyms` is DERIVED from `synonymSources` exactly the way the dashboard
    // does it (normalizeChain keys, normalized targets), so the shop sees the
    // same entries in the dashboard that the engine uses.
    synonymSources: BAZAAR_SYNONYM_SOURCES,
    synonyms: deriveSynonyms(BAZAAR_SYNONYM_SOURCES),
    // Two-word searches a word synonym cannot fix (dashboard rule: anything that
    // is not one clean word goes here). Fires on the exact whole search.
    //   • «μαύρη σοκολάτα» — Greek shops sell dark chocolate as «σοκολάτα
    //     υγείας» (16 bars); the literal phrase only finds biscuits.
    //   • «κόκα κόλα» — Greek spelling of a brand whose 23 titles say Coca Cola.
    //   • wine by colour — titles say «Λευκός / Ερυθρός / Ροζέ Οίνος», never
    //     «κρασί», and red is never «κόκκινο». Two substituted words in one
    //     search barely fire as word synonyms (measured: κόκκινο κρασί 35 → 40),
    //     so these run the real search instead.
    phraseSynonyms: {
      'λευκό κρασί': ['λευκός οίνος'],
      'κόκκινο κρασί': ['ερυθρός οίνος'],
      'ροζέ κρασί': ['ροζέ οίνος'],
      'μαύρη σοκολάτα': ['σοκολάτα υγείας'],
      'κόκα κόλα': ['coca cola'],
      // The hyphen and the one-word spelling are different searches (digital4u, 2026-10-12).
      'κόκα-κόλα': ['coca cola'],
      'κοκακόλα': ['coca cola'],
    },
    queryStopWords: [
      'από', 'απο', 'με', 'για', 'σε', 'και', 'στο', 'στη', 'στα',
      'στον', 'στην', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
      'ο', 'η', 'το', 'οι', 'τα', 'ένα', 'ενα', 'μια', 'μία', 'ένας', 'ενας', 'προς',
    ],
    // The brand facet (968 brands) is kept Latin automatically; nothing extra
    // needed at birth.
    // «milko» (Δέλτα Milko) is read as Greek «μιλκο» → stem «μιλκ», Milka's synonym
    // key, and Milka outranked it. Kept Latin, it finds only Milko.
    greeklishKeepWords: ['milko'],
    pricePatterns: {
      maxPrice: /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|up to|under|below)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      minPrice: /(?:πάνω από|πανω απο|πανω|πάνω|από|απο|over|above)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      range: /(?:από|απο|from)?\s*(\d+(?:\.\d+)?)\s*(?:-|–|έως|εως|μέχρι|μεχρι|to)\s*(\d+(?:\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
    },
    // None at birth. The searchable description is either a short till-system
    // name or (for ~1,000 products) a marketing paragraph. The paragraphs are
    // short (≤255 chars) and searched LAST; add a phrase here only if a query
    // battery shows one dragging in unrelated products.
    noisyPhrases: [],
  },

  // ── Index Settings (Bucket 2 — takes effect only on a re-crawl) ──
  // Mirrored in bazaar.code.ts, which is what the DB-backed runtime merges in.
  indexSettings: {
    // `title` is the full product name; `description_short` is the
    // other text field (see PICK_TITLE_PER_PRODUCT), searched last so it rescues
    // abbreviations and internal codes without outranking a real name match.
    // `model` is the barcode (<mpn>, e.g. 8001630010904), so a scanned or typed
    // barcode finds its product.
    searchableAttributes: [
      'title', 'title_normalized',
      'brand',
      'category', 'category_normalized',
      'subcategory', 'subcategory_normalized',
      'id', 'model',
      'description_short', 'description_short_normalized',
    ],
    // MUST stay ['*'] — enumerating it starves the agent-chat sidebar (b2a82f9).
    displayedAttributes: ['*'],
    // The «Προσφορές» / «Διατροφή» sidebar groups (fields from <tags>, see
    // tagFacets). Filterable from the crawl itself, so the facet rows can be
    // added AFTER the index already supports them — never a broken search.
    extraFilterableAttributes: ['offers', 'diet'],
    rankingRules: [
      'words', 'typo', 'proximity', 'attribute', 'sort', 'exactness',
    ],
    // Greek stemmer on the RAW fields only, never on the _normalized twins.
    localizedAttributes: [
      {
        locales: ['ell'],
        attributePatterns: ['title', 'category', 'subcategory', 'description_short'],
      },
    ],
    documentTemplate: '{{doc.category}} {{doc.subcategory}} - {{doc.brand}} {{doc.title}}',
    faceting: {
      maxValuesPerFacet: 500,
      sortFacetValuesBy: { '*': 'count' as const },
    },
    // 5/9: words under 5 letters get NO typo tolerance. A supermarket is full of
    // short words one letter apart that mean different things — «γάλα» (milk) /
    // «γάτα» (cat), «ρύζι» / «ρίζα». At oneTypo:4, a search for milk would pull in
    // cat food. Longer Greek words still forgive one typo.
    typoTolerance: {
      minWordSizeForTypos: { oneTypo: 5, twoTypos: 9 },
    },
  },

  // ── System Prompt ──
  systemPrompt: {
    categories: PRODUCT_CATEGORIES,
    productConcepts: PRODUCT_CONCEPTS,
    buildPrompt: buildBazaarPrompt,
  },

  // ── UI Configuration ──
  ui: {
    // Founder's spec (2026-10-01), revised the same day: the deep green
    // #004B30 as the brand read too dark, so the brand IS the button green.
    //   brand + buttons  RGB 2 127 82 = #027F52 (≈ their .button-cart #007F52)
    //   hover            RGB 0 75 48  = #004B30 (their --accent2), so the
    //                    product button still turns darker when hovered.
    // #027F52 is 5.0:1 against white: highlightColor and brandTextColor need no
    // override. cartButtonColor is not needed — the button IS the brand colour.
    brandColor: '#027F52',
    brandColorHover: '#004B30',
    // The 6px results/filters scrollbar keeps the deep green — founder's call.
    scrollbarColor: '#004B30',
    // Rounded card button, as on their own product cards (.product-thumb
    // .button-cart { border-radius: 10px }), asked for 2026-10-02.
    cartButtonRadius: 10,
    // Their feed's <tags> as one row of coloured blocks under the photo, the way
    // their own cards show them (founder picked option A, 2026-10-09).
    showProductTags: true,
    // The heart adds to their own «Αγαπημένα» (OpenCart wishlist.add; guests
    // too — kept in their session until they log in). 2026-10-09.
    favoritesFunction: 'wishlist.add',
    // Toggle + pre-filled hearts: digital4u made wishlist.remove work and added
    // a favourites-ids URL (their reply, 2026-10-12).
    favoritesRemoveFunction: 'wishlist.remove',
    favoritesIdsUrl: '/account/wishlist/ids',
    // wishlist.add pops their own white card mid-screen, which covered our
    // «Προστέθηκε» note (founder's screenshot, 2026-10-12) — remove it.
    favoritesHideShopNotice: '.alert-dismissible',
    // Pink-red heart (hover + added), founder 2026-10-09 — not the offer red.
    favoritesColor: '#E5365F',
    // Heart only for logged-in shoppers (founder, 2026-10-09): their header has
    // the OpenCart logout link only when logged in; guests see «Σύνδεση».
    // Matches both link forms — beta writes the short /account/logout, not
    // index.php?route=account/logout (founder's console check, 2026-10-12).
    favoritesLoggedInSelector: 'a[href*="account/logout"]',
    // Their --accent1, a near-black aubergine (18.6:1) used for text.
    productTitleColor: '#180E27',
    // Their OpenCart placeholder (HTTP 200). Only 1 for-sale product has no
    // photo, but realImageUrl() blanks placeholders, so this puts it back.
    fallbackImageUrl: 'https://www.bazaar-online.gr/image/no_image.png',
    // Their site uses Gotham Greek ('gotham_greek'); we already ship it.
    fontCssVariable: '--font-gotham-greek',
    categoriesToHide: new Set<string>(),
    exampleQueries: [
      { emoji: '🍝', text: 'Τι χρειάζομαι για μακαρονάδα με κιμά;', description: 'Recipe shopping' },
      { emoji: '🥛', text: 'Γάλα χωρίς λακτόζη', description: 'Dairy' },
      { emoji: '🧺', text: 'Υγρό πλυντηρίου για σκούρα ρούχα', description: 'Household' },
      { emoji: '🍷', text: 'Λευκό κρασί μέχρι 10 ευρώ', description: 'Wine' },
      { emoji: '🐱', text: 'Τροφή για γάτα', description: 'Pet food' },
    ],
    // Starting merchant content — the shop edits these in the dashboard.
    // Chosen from the largest shelves of the live catalogue.
    topSearches: ['γάλα', 'καφές', 'απορρυπαντικό', 'σοκολάτα', 'μπύρα'],
    // Entry-screen «Δημοφιλή Προϊόντα» — picked by eye for colour and spread
    // across departments (founder's ask, 2026-10-01): 2 rows of 4, no colour
    // repeated in a column — bananas, Coca-Cola, Milka, Fairy / Monster,
    // oranges, Ariel, Nutella. EIGHT, not more: the dashboard editor is a fixed
    // 8-slot grid and caps saves at 8, so extra codes would be silently dropped
    // the first time the shop saves. Merchant-owned: live in merchant_config
    // (+ draft) on dev and prod; the shop edits it in the dashboard.
    popularProductCodes: [
      '19213', '54491472', '5998711723491', '4084500854802',
      '5060639121670', '19082', '8700216804288', '80135876',
    ],
    strings: {
      // Example-query style (B2C), short enough for a 375px phone.
      searchPlaceholder: [
        'Γάλα χωρίς λακτόζη',
        'Καφές φίλτρου',
        'Απορρυπαντικό πλυντηρίου',
        'Σοκολάτα γάλακτος',
        'Τροφή για γάτα',
      ],
      brandLabel: 'Brand',
      filtersTitle: 'Φίλτρα',
      suggestedCategories: 'Προτεινόμενες Κατηγορίες',
      otherCategories: 'Άλλες Κατηγορίες',
      priceRange: 'Εύρος Τιμής',
      productsTitle: 'Προϊόντα',
      noResults: 'Δε βρέθηκαν προϊόντα',
      noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις - κλειδιά ή πιο γενικούς όρους αναζήτησης',
      showLess: 'Λιγότερα',
      showMore: (n: number) => `+${n} ακόμα...`,
      popularProducts: 'Δημοφιλή Προϊόντα',
      searchingSuggestions: 'Αναζήτηση Προτάσεων',
      searchingProducts: 'Αναζήτηση προϊόντων...',
      addToCart: 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ',
      topSearchesTitle: 'Top Αναζητήσεις',
      aiAssistantTitle: 'AI Βοηθός',
      aiAssistantGreeting: 'Πώς μπορώ να σας βοηθήσω; Ρωτήστε με για προϊόντα ή ιδέες για το καλάθι σας.',
    },
  },
};
